/**
 * 交易时间服务
 * 用于调用后端接口获取交易时间状态
 * 优先判断放假时间，如果在放假时间内用放假的描述，如果不在放假时间就用非放假时间的描述（都是在不开盘的状态下）
 */

import { get } from './axios.js';

// 交易时间接口地址（相对路径，会自动使用 baseURL）
const TRADING_STATUS_API = '/app/trading/status';

// 缓存配置，避免频繁请求
let cachedConfig = null;
let cacheTimestamp = null;
const CACHE_DURATION = 1 * 60 * 1000; // 缓存5分钟

/**
 * 调用交易时间状态接口
 * @returns {Promise<Object>} 返回接口响应数据
 */
async function fetchTradingStatus() {
  try {
    const response = await get(TRADING_STATUS_API);
    
    // get 函数返回的是 axios response 对象，需要访问 response.data
    const data = response?.data || response;
    
    if (data && data.code === 0) {
      return data;
    }
    
    throw new Error(data?.msg || '获取交易时间状态失败');
  } catch (error) {
    console.error('获取交易时间状态失败:', error);
    throw error;
  }
}

/**
 * 检查当前日期是否在放假时间范围内
 * @param {string} holidayStartTime - 放假开始时间 (格式: YYYY-MM-DD)
 * @param {string} holidayEndTime - 放假结束时间 (格式: YYYY-MM-DD)
 * @returns {boolean} 是否在放假时间内
 */
function isInHolidayRange(holidayStartTime, holidayEndTime) {
  if (!holidayStartTime || !holidayEndTime) {
    return false;
  }
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const startDate = new Date(holidayStartTime);
  const endDate = new Date(holidayEndTime);
  
  // 设置时间为0点，只比较日期
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  
  return today >= startDate && today <= endDate;
}

/**
 * 获取交易时间状态（带缓存）
 * @param {boolean} forceRefresh - 是否强制刷新缓存
 * @returns {Promise<Object>} 返回交易时间状态 {open: boolean, description: string, config?: Object}
 */
export async function getTradingStatus(forceRefresh = false) {
  // 检查缓存
  if (!forceRefresh && cachedConfig && cacheTimestamp) {
    const now = Date.now();
    if (now - cacheTimestamp < CACHE_DURATION) {
      return cachedConfig;
    }
  }
  
  try {
    const response = await fetchTradingStatus();
    
    // 优先使用接口返回的 status
    let result = {
      open: response.status?.open || false,
      description: response.status?.description || '当前为非开盘时间',
      config: null
    };
    
    // 如果有配置信息，进行详细判断
    if (response.configs && response.configs.length > 0) {
      const config = response.configs[0]; // 使用第一个配置
      
      // 如果接口返回不开盘，优先判断放假时间来决定描述
      if (!result.open) {
        const inHoliday = isInHolidayRange(config.holidayStartTime, config.holidayEndTime);
        result.isHoliday = inHoliday; // 添加是否在放假时间内的标识
        
        if (inHoliday) {
          // 在放假时间内，使用放假描述
          result.description = config.holidayDesc || response.status?.description || '当前为节假日休市';
        } else {
          // 不在放假时间，使用非开盘时间描述
          result.description = config.closedDesc || response.status?.description || '当前为非开盘时间';
        }
      } else {
        // 如果开盘，使用接口返回的描述
        result.description = response.status?.description || '当前为开盘时间';
        result.isHoliday = false;
      }
      
      result.config = config;
    } else {
      result.isHoliday = false;
    }
    
    // 更新缓存
    cachedConfig = result;
    cacheTimestamp = Date.now();
    
    return result;
  } catch (error) {
    // 如果接口调用失败，返回默认值
    console.warn('获取交易时间状态失败，使用默认值:', error);
    return {
      open: false,
      description: '当前为非开盘时间',
      config: null
    };
  }
}

/**
 * 检查当前时间是否在交易时间内
 * @param {boolean} forceRefresh - 是否强制刷新缓存
 * @returns {Promise<Object>} 返回检查结果 {isTradingTime: boolean, message: string}
 */
export async function checkTradingTime(forceRefresh = false) {
  const status = await getTradingStatus(forceRefresh);
  
  return {
    isTradingTime: status.open,
    message: status.description
  };
}

/**
 * 清除缓存
 */
export function clearCache() {
  cachedConfig = null;
  cacheTimestamp = null;
}

/**
 * 获取交易时间配置信息
 * @param {boolean} forceRefresh - 是否强制刷新缓存
 * @returns {Promise<Object>} 返回交易时间配置对象
 */
export async function getTradingTimeConfig(forceRefresh = false) {
  const status = await getTradingStatus(forceRefresh);
  return status.config;
}

